/**
 * @fileoverview Loader component for displaying loading states with optional timeout functionality
 * @module Loader
 * @requires prop-types
 * @requires react
 * @requires @smarterservices/smarter-ui
 */

import PropTypes from 'prop-types';
import React, { useEffect, useState, forwardRef } from 'react';
import { Mask, Spinner, Text, Flex } from '@smarterservices/smarter-ui';

/**
 * A flexible loading component that displays a spinner with optional text
 * and supports timeout functionality for handling long-running operations.
 *
 * @function Loader
 * @param {Object} props - The component props
 * @param {boolean} [props.enabled=true] - Whether the loader is visible
 * @param {('x-small'|'small'|'medium'|'large')} [props.spinnerSize='large'] - Size of the spinner
 * @param {('small'|'medium'|'large')} [props.textSize='large'] - Size of the text
 * @param {string} [props.loaderTitle='Loading'] - Title for screen readers
 * @param {string} [props.text=''] - Optional text to display below the loader
 * @param {string} [props.textColor='primary'] - Color of the text
 * @param {('div'|'span'|'p'|'h1'|'h2'|'h3'|'h4'|'h5'|'h6')} [props.textAs='div'] - Type of element to use for text
 * @param {boolean} [props.maskPage=false] - Whether to show a full screen mask
 * @param {string} [props.spinnerMargin='0'] - Margin of the spinner
 * @param {number} [props.timeout=0] - Number of seconds before timeout (0 = no timeout)
 * @param {Function} [props.onTimeout=null] - Function called when timeout is reached, can return content to replace the spinner
 * @returns {React.ReactElement|null} The rendered component or null if disabled
 */
const Loader = forwardRef(({
  enabled = true,
  spinnerSize = 'large',
  textSize = 'large',
  loaderTitle = 'Loading',
  text = '',
  textColor = 'primary',
  textAs = 'div',
  maskPage = false,
  spinnerMargin = '0',
  timeout = 0,
  onTimeout = null
}, ref) => {
  if (!enabled) return null;

  /**
   * Validate that if timeout is set, onTimeout function is also provided
   * @throws {Error} If timeout is set without onTimeout function
   */
  if (timeout > 0 && !onTimeout) {
    throw new Error('Loader: onTimeout function must be provided when timeout is set');
  }

  /**
   * State to track if the loader has timed out
   * @type {[boolean, Function]}
   */
  const [isTimedOut, setIsTimedOut] = useState(false);

  /**
   * State to store custom content returned by onTimeout
   * @type {[React.ReactElement|null, Function]}
   */
  const [timeoutContent, setTimeoutContent] = useState(null);

  /**
   * Effect to handle the timeout functionality
   * Sets up a timer when the component mounts and cleans it up when unmounting
   */
  useEffect(() => {
    let timeoutId;

    if (enabled && timeout > 0) {
      // We can safely access onTimeout here since we've validated it exists if timeout > 0
      timeoutId = setTimeout(() => {
        setIsTimedOut(true);
        const customContent = onTimeout();
        if (customContent) {
          setTimeoutContent(customContent);
        }
      }, timeout * 1000);
    }

    // Cleanup function to clear the timeout when component unmounts
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [enabled, timeout, onTimeout]);

  /**
   * Render custom timeout content if timeout has occurred and content is available
   */
  if (isTimedOut && timeoutContent) {
    if (maskPage) {
      return (
        <Mask
          placement="center"
          fullscreen={true}
          themeOverride={{ zIndex: 9999 }}
          ref={ref}
        >
          {timeoutContent}
        </Mask>
      );
    }
    return React.cloneElement(timeoutContent, { ref });
  }

  /**
   * Default loader content with spinner and optional text
   * @type {React.ReactElement}
   */
  const content = (
    <Flex
      direction="column"
      alignItems="center"
      justifyItems="center"
      height="100%"
      ref={!maskPage ? ref : undefined}
    >
      <Spinner renderTitle={loaderTitle} size={spinnerSize} margin={spinnerMargin} />
      {text && (
        <Text as={textAs} size={textSize} color={textColor}>
          {text}
        </Text>
      )}
    </Flex>
  );

  /**
   * Return the content directly or wrapped in a Mask based on maskPage prop
   */
  if (!maskPage) {
    return content;
  }

  return (
    <Mask
      placement="center"
      fullscreen={true}
      themeOverride={{ zIndex: 9999 }}
      ref={ref}
    >
      {content}
    </Mask>
  );
});

Loader.propTypes = {
  /** Whether the loader is visible */
  enabled: PropTypes.bool,
  /** Size of the loader icon */
  spinnerSize: PropTypes.oneOf(['x-small', 'small', 'medium', 'large']),
  /** Size of the text */
  textSize: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Type of element to use for text */
  textAs: PropTypes.oneOf(['div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /** Title for screen readers */
  loaderTitle: PropTypes.string,
  /** Optional text to display below the loader */
  text: PropTypes.string,
  /** Color of the text */
  textColor: PropTypes.string,
  /** Margin of the spinner */
  spinnerMargin: PropTypes.string,
  /** Whether to show a full screen mask */
  maskPage: PropTypes.bool,
  /** Number of seconds before timeout (0 = no timeout) */
  timeout: PropTypes.number,
  /** Function called when timeout is reached, can return content to replace the spinner */
  onTimeout: PropTypes.func
};

Loader.defaultProps = {
  enabled: true,
  spinnerSize: 'large',
  textSize: 'large',
  loaderTitle: 'Loading',
  text: '',
  textColor: 'primary',
  textAs: 'div',
  spinnerMargin: '0',
  maskPage: false,
  timeout: 0,
  onTimeout: null
};

/**
 * Export the Loader component as the default export
 */
export default Loader;
